/**
 * commandHandler.ts
 * GB→ESP コマンドを受け取り、適切なレスポンスをエミュレートする。
 *
 * 通信プロトコル（ESP視点）:
 *   - GBはコマンド文字列（\0終端）を0x7FFFに2回ずつ書き込む
 *   - Rustレイヤーは完成コマンドを1件だけ保持し、HTTP待機中は取り出さない
 *   - JSは処理してから esp8266PushResponse(bytes) でレスポンスをRustに注入する
 *   - GBは0x7FFEから1バイトずつ読み出す（先頭バイトは2回読む）
 *
 * パケットフォーマット (writetoGB相当):
 *   P{seqNo:'0'|'1'}{checksum:2hexUPPER}{data}\0
 *
 * テキストチャンク (T-packet):
 *   T{API=8}{SUCCESS=1}{signal}{7bit_encoded_text}\0 → wrappedInPacket
 *
 * 各コマンドのレスポンス方針:
 *   SSID  → モックSSIDリスト (S{7bitData}\n...)
 *   SYNC  → ファームウェアの実行位置に応じて番号リセット・本文再開・現在行再送
 *   GTXR  → テキストリストからT-chunks + TEND
 *   GDAT  → D-chunks + END
 *   WIFI  → WAIT → (非同期500ms後) EWIFI SUCCESS
 *   WCHK  → WAIT or EWIFI SUCCESS
 *   NAME  → WAIT → E NAME SUCCESS/FAILURE
 *   NRES  → 直近のNAME結果を再送
 *   TALK  → WAIT → T-chunks + TEND
 *   ARES  → 待機中の保存済みAPI結果（現行ファームウェアの空レスポンスも再現）
 *   GMTA  → META
 *   GPAG  → LINE-chunks + PEND
 */
import {
    callDifyAPI,
    cancelActiveDifyRequests,
    clearConversationId,
    ensureUserId,
    getConversationId,
} from "./difyClient";
import { encode7bit, encodeText, wrapDataTo7bit } from "./encoding";
import { buildPacket } from "./packet";
import {
    appendFixedHistory,
    appendHistory,
    getMemoryCounts,
    getUserId,
    getUsername,
    resetGameData,
    setUsername,
    setUserId,
    updateMemoryProgress,
} from "./persistentState";
import {
    getBinaryData,
    getCurrentLanguage,
    getTextByIndex,
    getTextDumpSize,
    setLanguage,
} from "./textData";

// commwords.h の定数
const API = 8;
const NAME = 9;
const WIFI = 7;
const SUCCESS = 1;
const FAILURE = 2;

// 実行中のファームウェア処理がHTTP・ACK待機を終えるまでコマンドループを占有する。
// 転送中のSYNCで番号が変わるため、パケットのヘッダーは送信時に付ける。
let seqNo: 0 | 1 = 0;
type TransferKind = "text" | "api" | "data" | "page";
interface Transfer {
    kind: TransferKind;
    payloads: Uint8Array[];
    rawData?: Uint8Array;
    index: number;
    terminal: "TEND" | "END" | "PEND";
    packet: Uint8Array | null;
    elapsedMs: number;
}
let transfer: Transfer | null = null;
let lastNamePayload: Uint8Array | null = null;
let hasApiResponse = false;
let httpBlocked = false;
let httpCompletion: (() => void) | null = null;
let asyncGeneration = 0;
let firmwareFault: string | null = null;

type WifiState = "idle" | "connecting" | "connected" | "failed";
let wifiState: WifiState = "idle";
let wifiElapsedMs = 0;
let wifiResultPending = false;
const MOCK_SIGNAL_CLASS = 3;
const MOCK_SIGNAL_ASCII = 0x30 + MOCK_SIGNAL_CLASS;
const MAX_STORY_LINES = 600;
const MAX_TEXT_PACKETS = 600;
const ACK_TIMEOUT_MS = 2000;

// 実機相当の出力枠と完成済みコマンドの入力枠はRust側で保持する。
type PushResponseFn = (bytes: Uint8Array) => void;
let pushResponseFn: PushResponseFn | null = null;
let discardPendingCommandFn: (() => void) | null = null;

export function setPushResponseFn(fn: PushResponseFn): void {
    pushResponseFn = fn;
}

function pushResponse(bytes: Uint8Array): void {
    pushResponseFn?.(bytes);
}

function nextSeqNo(): void {
    seqNo = seqNo === 0 ? 1 : 0;
}

function resetSeqNo(): void {
    seqNo = 0;
}

/** 通知にはACKがないため、通信番号を消費しない。 */
function sendPacket(data: Uint8Array): void {
    const isTransient = data[0] === 0x45 && data[1] === 4;
    if (isTransient) {
        pushResponse(Uint8Array.from([...data.slice(0, 255), 0]));
    } else {
        pushResponse(buildPacket(seqNo, data));
        nextSeqNo();
    }
}

function sendStringPacket(value: string): void {
    const data = new TextEncoder().encode(value);
    if (value === "WAIT" || value === "BUSY") {
        pushResponse(Uint8Array.from([...data, 0]));
    } else {
        sendPacket(data);
    }
}

function transferPayload(active: Transfer): Uint8Array | null {
    if (active.rawData) {
        const position = active.index * 120;
        if (position >= active.rawData.length) return null;
        const encoded = wrapDataTo7bit(
            active.rawData.slice(position, position + 120),
        );
        return Uint8Array.from([0x44, ...encoded]);
    }
    return active.payloads[active.index] ?? null;
}

function sendTransferPacket(): void {
    if (!transfer) return;
    const payload = transferPayload(transfer);
    if (!payload) {
        const { kind, terminal } = transfer;
        transfer = null;
        sendStringPacket(terminal); // 実機は終端をACKなしで一度だけ送信する。
        if (kind === "api") hasApiResponse = true;
        return;
    }
    transfer.packet = buildPacket(seqNo, payload);
    nextSeqNo();
    transfer.elapsedMs = 0;
    pushResponse(transfer.packet);
}

function retryTransferPacket(): void {
    if (transfer?.packet) {
        transfer.elapsedMs = 0;
        pushResponse(transfer.packet);
    }
}

function handleTransferCommand(header: string, bytes: Uint8Array): void {
    if (!transfer) return;
    const isData = transfer.kind === "data";
    if (header === (isData ? "DSUC" : "TSUC")) {
        transfer.index++;
        sendTransferPacket();
    } else if (
        isData ? header === "DFAL" : header === "TFAI" && bytes[4] === 0x4c
    ) {
        retryTransferPacket();
    } else if (header === "SYNC") {
        resetSeqNo();
        // sendTextPacketReliablyは応答全体を最初から送り直す。
        // 他の2種類のACK待機処理は、現在のチャンク・行だけを再送する。
        if (transfer.kind === "text" || transfer.kind === "api")
            transfer.index = 0;
        sendTransferPacket();
    }
    // 実機のACK待機処理は、それ以外のコマンドを読み捨てる。
}

/** HTTP待機中、Rustは完成済みコマンド1件を入力枠に保持する。 */
export function canPollEsp8266Command(): boolean {
    return !httpBlocked && !firmwareFault;
}

/** 実機の不正書込みは、メモリ破壊を再現せず異常として報告する。 */
export function getEsp8266FirmwareFault(): string | null {
    return firmwareFault;
}

function beginHttp(
    operation: () => Promise<string>,
    complete: (answer: string) => void,
): void {
    const generation = asyncGeneration;
    httpBlocked = true;
    // エミュレーション停止中は、ブラウザでHTTPが完了しても処理を進めない。
    void operation().then(
        answer => {
            if (generation !== asyncGeneration) return;
            httpCompletion = () => {
                httpBlocked = false;
                complete(answer);
            };
        },
        () => {
            if (generation !== asyncGeneration) return;
            // 実機はHTTP失敗時にそのまま戻り、
            // 会話文や認証成功の応答を生成しない。
            httpCompletion = () => {
                httpBlocked = false;
            };
        },
    );
}

/** ブラウザの実時間タイマーではなく、エミュレータの時計からのみ呼び出す。 */
export function advanceEsp8266Time(elapsedMs: number): void {
    if (!Number.isFinite(elapsedMs) || elapsedMs < 0) return;
    if (firmwareFault) return;
    if (httpCompletion) {
        const complete = httpCompletion;
        httpCompletion = null;
        complete();
        // 今送信したパケットの経過時間に、この直前の時間区間は含めない。
        return;
    }
    if (httpBlocked) return;
    if (wifiState === "connecting") {
        wifiElapsedMs += elapsedMs;
        if (wifiElapsedMs >= 500) wifiState = "connected";
    }
    if (!transfer) return;
    transfer.elapsedMs += elapsedMs;
    if (transfer.elapsedMs <= ACK_TIMEOUT_MS) return;
    if (transfer.kind === "data") {
        transfer.index++; // 現行の実機はACKが失われても次へ進む。
        sendTransferPacket();
    } else if (transfer.kind === "page") {
        transfer = null; // sendPageToGBはPENDを送らず終了処理へ進む。
    } else {
        retryTransferPacket();
    }
}

/** ROM・ESPの切替時にJS側のセッションを破棄する。通信上のSYNCとは区別する。 */
export function resetEsp8266Session(): void {
    asyncGeneration++;
    cancelActiveDifyRequests();
    resetSeqNo();
    transfer = null;
    httpBlocked = false;
    httpCompletion = null;
    lastNamePayload = null;
    hasApiResponse = false;
    wifiState = "idle";
    wifiElapsedMs = 0;
    wifiResultPending = false;
    firmwareFault = null;
}

function sendNameResult(status: typeof SUCCESS | typeof FAILURE): void {
    lastNamePayload = new Uint8Array([0x45, NAME, status]);
    sendPacket(lastNamePayload);
}

function fixedHistoryText(text: string): string {
    const firstDelimiter = text.indexOf("##");
    if (firstDelimiter < 0) return text;
    const secondDelimiter = text.indexOf("##", firstDelimiter + 2);
    return secondDelimiter < 0 ? text : text.slice(0, secondDelimiter);
}

function answerHistoryText(answer: string): string {
    const firstDelimiter = answer.indexOf("##");
    if (firstDelimiter < 0) return answer;
    const secondDelimiter = answer.indexOf("##", firstDelimiter + 2);
    return secondDelimiter < 0 ? answer : answer.slice(0, secondDelimiter);
}

/** バイナリ素材は一括変換せず、現在の120バイト分だけを変換する。 */
function sendDataChunks(rawData: Uint8Array): void {
    transfer = {
        kind: "data",
        payloads: [],
        rawData,
        index: 0,
        terminal: "END",
        packet: null,
        elapsedMs: 0,
    };
    sendTransferPacket();
}

/**
 * チャンク末尾が0x0B（8bitヘッダ）で終わらないよう調整する (getSafeChunkSize相当)
 * ESPの現行実装と同じく0x0Bをチェックする。
 */
function getSafeChunkSize(
    pos: number,
    totalLen: number,
    maxChunk: number,
    as7bit: Uint8Array,
): number {
    let size = Math.min(maxChunk, totalLen - pos);
    while (
        pos + size < totalLen &&
        size > 0 &&
        as7bit[pos + size - 1] === 0x0b
    ) {
        size--;
    }
    // 0x0Bだけが連続する不正入力でも必ず前進させる。実機では入力長が
    // RAM容量で自然に制限されるが、ブラウザでは無限ループを明示的に防ぐ。
    return Math.max(1, size);
}

/** 固定テキストとHTTP応答で異なるチャンク境界を維持する。 */
function sendTextChunks(
    encoded: Uint8Array,
    maxChunk: number,
    api = false,
): void {
    const as7bit = encode7bit(encoded);
    const payloads: Uint8Array[] = [];
    let pos = 0;
    while (pos < as7bit.length && payloads.length < MAX_TEXT_PACKETS - 1) {
        const chunkSize = api
            ? Math.min(maxChunk, as7bit.length - pos)
            : getSafeChunkSize(pos, as7bit.length, maxChunk, as7bit);
        payloads.push(
            Uint8Array.from([
                0x54,
                API,
                SUCCESS,
                MOCK_SIGNAL_ASCII,
                ...as7bit.slice(pos, pos + chunkSize),
            ]),
        );
        pos += chunkSize;
    }
    transfer = {
        kind: api ? "api" : "text",
        payloads,
        index: 0,
        terminal: "TEND",
        packet: null,
        elapsedMs: 0,
    };
    sendTransferPacket();
}

/** SSIDパケットを組み立てて送信 */
function buildAndSendSSIDPacket(): void {
    const ssids = ["Daydream_Wi-Fi"];

    // S + 各SSID(7bitエンコード) + \n
    const parts: number[] = [0x53]; // 'S'
    for (let si = 0; si < ssids.length; si++) {
        const code = encodeText(ssids[si]);
        const as7bit = encode7bit(code);
        for (let bi = 0; bi < as7bit.length; bi++) parts.push(as7bit[bi]);
        parts.push(0x0a); // '\n'
    }
    sendPacket(new Uint8Array(parts));
}

/** コマンドの先頭4文字を取得 */
function getHeader(bytes: Uint8Array): string {
    return new TextDecoder().decode(bytes.slice(0, 4));
}

/** HEX ASCII バイト列を数値に変換 */
function parseHexBytes(bytes: Uint8Array, offset: number, len: number): number {
    let hexStr = "";
    for (let i = 0; i < len; i++) {
        hexStr += String.fromCharCode(bytes[offset + i] ?? 0);
    }
    return parseInt(hexStr, 16) || 0;
}

/** 数値を大文字HEX文字列に変換 */
function hex2(n: number): string {
    return n.toString(16).padStart(2, "0").toUpperCase();
}
function hex4(n: number): string {
    return n.toString(16).padStart(4, "0").toUpperCase();
}

function getJstMonthDay(): [number, number] {
    const jst = new Date(Date.now() + 9 * 60 * 60 * 1000);
    return [jst.getUTCMonth() + 1, jst.getUTCDate()];
}

/**
 * テキストインデックス範囲からストーリー行配列を構築 (buildStoryIndex相当)
 * 18文字で折り返し、## (0x03 0x03) または \n (0x0a) で改行。
 */
interface StoryLine {
    bytes: Uint8Array;
    sendable: boolean;
}

function buildStoryLines(baseIdx: number, chunks: number): StoryLine[] {
    const lines: StoryLine[] = [];
    const storedUsername = getUsername();
    for (let i = 0; i < chunks; i++) {
        let text = getTextByIndex(baseIdx + i);
        if (!text) break;
        // "user" プレースホルダーをユーザー名に置換（pageIndex.ino parseJPto1byte expandUser=true 相当）
        if (storedUsername && storedUsername.length > 0) {
            text = text.split("user").join(storedUsername);
        }
        const encoded = encodeText(`${i > 0 ? "\n" : ""}${text}`);
        let currentLine: number[] = [];
        let charCount = 0;
        for (let j = 0; j < encoded.length; j++) {
            const c = encoded[j];
            // ## separator: 0x03 0x03
            if (
                c === 0x03 &&
                j + 1 < encoded.length &&
                encoded[j + 1] === 0x03
            ) {
                lines.push({
                    bytes: new Uint8Array(currentLine),
                    sendable: true,
                });
                if (lines.length >= MAX_STORY_LINES) return lines;
                currentLine = [];
                charCount = 0;
                j++;
                continue;
            }
            // \n separator
            if (c === 0x0a) {
                lines.push({
                    bytes: new Uint8Array(currentLine),
                    sendable: true,
                });
                if (lines.length >= MAX_STORY_LINES) return lines;
                currentLine = [];
                charCount = 0;
                continue;
            }
            // 濁点・半濁点は文字数にカウントしない
            if (c === 254 || c === 255) {
                currentLine.push(c);
                continue;
            }
            // 18文字で強制折り返し
            if (charCount >= 18) {
                lines.push({
                    bytes: new Uint8Array(currentLine),
                    sendable: true,
                });
                if (lines.length >= MAX_STORY_LINES) return lines;
                currentLine = [c];
                charCount = 1;
                continue;
            }
            currentLine.push(c);
            charCount++;
        }
        // 実機は各チャンクの先頭行を必ず登録し、末尾が改行なら
        // その後ろの空行も1行として数える。
        lines.push({
            bytes: new Uint8Array(currentLine),
            // sendPageToGBはチャンク末尾の空行をMETAには数えるが、
            // LINEパケットとしては送らない。
            sendable: currentLine.length > 0,
        });
        if (lines.length >= MAX_STORY_LINES) return lines;
    }
    return lines;
}

/**
 * GBから送られたコマンドを処理する。
 * Rustの esp8266_on_command から呼び出される。
 */
export function handleCommand(bytes: Uint8Array): void {
    if (bytes.length < 4) return;
    // 直接呼び出す場合も、Rustとの接続時と同じ受信可否の条件に従う。
    if (!canPollEsp8266Command()) return;
    const header = getHeader(bytes);
    if (transfer) {
        handleTransferCommand(header, bytes);
        return;
    }

    switch (header) {
        case "SYNC": {
            resetSeqNo();
            break;
        }

        case "LANG": {
            // LANG{lang:1byte} → 0=日本語, 1=英語
            resetSeqNo();
            const lang = (bytes[4] ?? 0) as 0 | 1;
            setLanguage(lang);
            break;
        }

        case "SSID": {
            resetSeqNo();
            buildAndSendSSIDPacket();
            break;
        }

        case "GTXR": {
            resetSeqNo();
            if (bytes.length < 8) break;
            const b1 = bytes[4] ?? 0;
            const b2 = bytes[5] ?? 0;
            const b1Complement = bytes[6] ?? 0;
            const b2Complement = bytes[7] ?? 0;
            if (
                ((b1 ^ b1Complement) & 0x7f) !== 0x7f ||
                ((b2 ^ b2Complement) & 0x7f) !== 0x7f
            ) {
                break;
            }
            const idx = (b1 & 0x7f) | ((b2 & 0x7f) << 7);
            if (idx !== 999 && (idx === 0 || idx > getTextDumpSize())) break;

            // idx=1のリクエストでデータ初期化（実機 clearAllDataFiles 相当）
            if (idx === 1) {
                clearConversationId();
                resetGameData();
            }

            let text = getTextByIndex(idx);
            // "user" プレースホルダーをユーザー名に置換（textEngine.ino parseJPto1byte expandUser 相当）
            const storedUsername = getUsername();
            if (storedUsername && storedUsername.length > 0) {
                text = text.split("user").join(storedUsername);
            }
            if (idx !== 999) {
                const internalIndex = idx - 1;
                updateMemoryProgress(internalIndex);
                if (
                    (internalIndex >= 1 && internalIndex <= 40) ||
                    (internalIndex >= 240 && internalIndex <= 322)
                ) {
                    appendFixedHistory(fixedHistoryText(text));
                }
            }
            const encoded = encodeText(text);
            sendTextChunks(encoded, 240);
            break;
        }

        case "GDAT": {
            // GDAT{dtype}{idx}\0
            // dtype: 'T'=tile, 'M'=map, 'S'=sound
            // idx: 1-based binary value
            resetSeqNo();
            const dtype = String.fromCharCode(bytes[4] ?? 0);
            const idx = bytes[5] ?? 1;
            const rawData = getBinaryData(dtype, idx);
            if (rawData && rawData.length > 0) {
                sendDataChunks(rawData);
            } else {
                // 実機は素材が存在しないか不正な場合、応答せずに戻る。
            }
            break;
        }

        case "GMTA": {
            // GMTA{idx:4hex}{chunks:2hex} → META{pages:2hex}{lines:4hex}
            // GMTA/GPAGのidxはGTXRと異なりESP側でidx--しないため0オリジン。
            // getTextByIndexは1オリジンなので+1して補正する。
            resetSeqNo();
            const gmtaIdx = parseHexBytes(bytes, 4, 4);
            const gmtaChunks = parseHexBytes(bytes, 8, 2);
            const gmtaLines = buildStoryLines(gmtaIdx + 1, gmtaChunks);
            const totalLines = gmtaLines.length;
            const totalPages = Math.ceil(totalLines / 12);
            sendStringPacket(`META${hex2(totalPages)}${hex4(totalLines)}`);
            break;
        }

        case "GPAG": {
            // GPAG{idx:4hex}{page:2hex}{chunks:2hex} → LINE×n + PEND
            // GMTAと同様にidxは0オリジンなので+1して補正する。
            resetSeqNo();
            const gpagIdx = parseHexBytes(bytes, 4, 4);
            const gpagPage = parseHexBytes(bytes, 8, 2);
            const gpagChunks = parseHexBytes(bytes, 10, 2);
            const gpagLines = buildStoryLines(gpagIdx + 1, gpagChunks);
            const gpagTotalPages = Math.ceil(gpagLines.length / 12);
            if (gpagPage >= gpagTotalPages) {
                sendStringPacket("PEND");
                break;
            }
            const startLine = gpagPage * 12;
            const pageLines = gpagLines
                .slice(startLine, startLine + 12)
                .filter(line => line.sendable);
            const payloads: Uint8Array[] = [];
            for (let i = 0; i < pageLines.length; i++) {
                const as7bit = encode7bit(pageLines[i].bytes);
                const hdr = new TextEncoder().encode(
                    `LINE${hex2(gpagPage)}${hex2(i)}${hex2(MOCK_SIGNAL_CLASS)}`,
                );
                const data = new Uint8Array(hdr.length + as7bit.length);
                data.set(hdr);
                data.set(as7bit, hdr.length);
                payloads.push(data);
            }
            transfer = {
                kind: "page",
                payloads,
                index: 0,
                terminal: "PEND",
                packet: null,
                elapsedMs: 0,
            };
            sendTransferPacket();
            break;
        }

        case "WIFI": {
            resetSeqNo();
            wifiState = "connecting";
            wifiElapsedMs = 0;
            wifiResultPending = true;
            sendStringPacket("WAIT");
            break;
        }

        case "WCHK":
        case "WRES": {
            if (wifiState === "connecting" && header === "WCHK") {
                sendStringPacket("WAIT");
            } else if (wifiState !== "connected") {
                sendPacket(new Uint8Array([0x45, WIFI, FAILURE]));
            } else {
                const sendConnected = (): void => {
                    if (header === "WCHK") wifiResultPending = false;
                    const [month, day] = getJstMonthDay();
                    sendPacket(
                        new Uint8Array([
                            0x45,
                            WIFI,
                            SUCCESS,
                            MOCK_SIGNAL_ASCII,
                            month,
                            day,
                        ]),
                    );
                };
                if (
                    header === "WRES" ||
                    !wifiResultPending ||
                    getUserId() !== "newuser"
                ) {
                    sendConnected();
                } else {
                    // 登録処理中も次の命令を処理しない。保留入力はTALK本文の
                    // 送信前とは異なり破棄しない。
                    // ユーザー登録要求が失敗してもWi-Fi自体の接続は成功しているため、
                    // 実機はWIFI SUCCESSを返す。
                    sendStringPacket("BUSY");
                    beginHttp(
                        () => ensureUserId().catch(() => "newuser"),
                        sendConnected,
                    );
                }
            }
            break;
        }

        case "NAME": {
            resetSeqNo();
            sendStringPacket("WAIT");
            const nameBytes = bytes.slice(4);
            const nameEnd = nameBytes.indexOf(0);
            const nameSlice =
                nameEnd >= 0 ? nameBytes.slice(0, nameEnd) : nameBytes;
            const username = codesToUtf8(decode7bitData(nameSlice)).trim();
            if (nameSlice.length === 0) {
                sendNameResult(FAILURE);
                break;
            }
            if (wifiState !== "connected") {
                sendNameResult(FAILURE);
                break;
            }
            const langStr = getCurrentLanguage() === 1 ? "english" : "japanese";
            const query = `##language:${langStr}\n##username:${username}`;
            const isAuthentication = !getConversationId();
            const isFirstCall = getUserId() === "newuser";
            sendStringPacket("BUSY");
            beginHttp(
                () =>
                    isFirstCall
                        ? callDifyAPI(query, undefined, {
                              persistConversation: false,
                          })
                        : callDifyAPI(query),
                answer => {
                    if (isFirstCall) {
                        if (answer) setUserId(answer);
                        return;
                    }
                    if (!isAuthentication) {
                        // 現行の実機は会話IDがあるNAMEを通常会話として処理する。
                        // NAMEの結果を返さなくなる動作も合わせる。
                        appendHistory(query.slice(0, query.lastIndexOf("##")));
                        appendHistory(answerHistoryText(answer));
                        if (answer.length > 0) {
                            discardPendingCommandFn?.();
                            sendTextChunks(encodeText(answer), 120, true);
                        }
                        return;
                    }
                    const newline = answer.indexOf("\n");
                    const answerLanguage =
                        newline >= 0 ? answer.slice(0, newline).trim() : "";
                    const answerUsername = (
                        newline >= 0 ? answer.slice(newline + 1) : answer
                    ).trim();
                    if (
                        answerLanguage === langStr &&
                        answerUsername === username &&
                        username.length > 0
                    ) {
                        setUsername(username);
                        sendNameResult(SUCCESS);
                    } else {
                        sendNameResult(FAILURE);
                    }
                },
            );
            break;
        }

        case "NRES": {
            // 実機は結果本体だけを保存し、ヘッダーや以前の通信番号は保持しない。
            if (lastNamePayload) sendPacket(lastNamePayload);
            else
                sendPacket(
                    Uint8Array.from([
                        0x45,
                        NAME,
                        FAILURE,
                        ...new TextEncoder().encode("No Response"),
                    ]),
                );
            break;
        }

        case "TALK": {
            // TALK{7bit_message}\0
            resetSeqNo();
            hasApiResponse = false;
            sendStringPacket("WAIT");

            // メッセージを1バイトコード → UTF-8テキストに変換
            const msgBytes = bytes.slice(4);
            const msgEnd = msgBytes.indexOf(0);
            const msgSlice = msgEnd >= 0 ? msgBytes.slice(0, msgEnd) : msgBytes;
            // 7bitデコード（1バイトコードのまま）
            const decoded7bit = decode7bitData(msgSlice);

            // 実機ESP相当のメッセージ組み立て
            // 実機ESPはcodeToUTF8前にASCIIで直接バイト比較する（main.ino: "vote はASCII文字列なのでデコードせずそのまま使用"）
            let msgText: string;
            if (decoded7bit.length === 0) {
                // 空メッセージ（むごん）: テキスト510番を使用
                msgText = getTextByIndex(511);
            } else if (
                decoded7bit.length === 4 &&
                decoded7bit[0] === 0x76 &&
                decoded7bit[1] === 0x6f &&
                decoded7bit[2] === 0x74 &&
                decoded7bit[3] === 0x65
            ) {
                // 投票フェーズ: "vote" はASCII文字列なのでcodeToUTF8せずそのまま使用
                msgText = "vote";
            } else {
                msgText = codesToUtf8(decoded7bit);
            }
            // 記憶解放カウンタを付与（実機: ##Player,Emi,Toru,Saki,Koji）
            const historyMessage = msgText;
            if (msgText.length > 0)
                msgText += `##${getMemoryCounts().join(",")}`;

            const debug = new TextEncoder().encode(
                `API Start: ${historyMessage}`,
            );
            if (debug.length + 3 > 128) {
                // ブラウザ内では実機のメモリ破壊を再現しない。
                firmwareFault =
                    "API Start exceeds the firmware's 128-byte debug buffer";
                console.warn(`[ESP8266] ${firmwareFault}`);
                break;
            }
            sendPacket(Uint8Array.from([0x45, 4, ...debug]));
            if (!msgText) {
                sendPacket(
                    Uint8Array.from([
                        0x45,
                        API,
                        FAILURE,
                        ...new TextEncoder().encode("No Message"),
                    ]),
                );
                break;
            }
            if (wifiState !== "connected") break;
            sendStringPacket("BUSY");
            const isFirstCall = getUserId() === "newuser";
            beginHttp(
                () =>
                    isFirstCall
                        ? callDifyAPI(msgText, undefined, {
                              persistConversation: false,
                          })
                        : callDifyAPI(msgText),
                answer => {
                    if (isFirstCall) {
                        if (answer) setUserId(answer);
                        return;
                    }
                    appendHistory(historyMessage);
                    appendHistory(answerHistoryText(answer));
                    if (answer.length > 0) {
                        // saveData.inoと同様に、http.POSTの待機中に割込み処理が
                        // 保持した入力1件だけを破棄する。
                        discardPendingCommandFn?.();
                        sendTextChunks(encodeText(answer), 120, true);
                    }
                },
            );
            break;
        }

        case "ARES": {
            resetSeqNo();
            if (hasApiResponse) {
                // 現行の実機は転送完了を記録してもlastApiResponseを空のままにする。
                // この再取得に失敗する動作も維持する。
                sendStringPacket("");
            } else {
                sendPacket(
                    Uint8Array.from([
                        0x45,
                        API,
                        FAILURE,
                        ...new TextEncoder().encode("No Response"),
                    ]),
                );
            }
            break;
        }

        default:
            // 未知のコマンドは無視
            break;
    }
}

/** wrapdataTo8bit相当: 7bitエンコードデータをデコード */
function decode7bitData(bytes: Uint8Array): Uint8Array {
    const result: number[] = [];
    let i = 0;
    while (i < bytes.length) {
        if (bytes[i] === 0x0b) {
            i++;
            if (i >= bytes.length) break;
            const v = bytes[i];
            if (v === 0x7d) result.push(0x80);
            else if (v === 0x7c) result.push(0x82);
            else result.push(v | 0x80);
        } else {
            result.push(bytes[i]);
        }
        i++;
    }
    return new Uint8Array(result);
}

/** GB 1バイトコード → UTF-8文字列 (codeToUTF8相当) */
function codesToUtf8(codes: Uint8Array): string {
    const JP_MAP: string[] = [
        "、",
        "。",
        "「",
        "」",
        "ぁ",
        "あ",
        "ぃ",
        "い",
        "ぅ",
        "う",
        "ぇ",
        "え",
        "ぉ",
        "お",
        "か",
        "き",
        "く",
        "け",
        "こ",
        "さ",
        "し",
        "す",
        "せ",
        "そ",
        "た",
        "ち",
        "っ",
        "つ",
        "て",
        "と",
        "な",
        "に",
        "ぬ",
        "ね",
        "の",
        "は",
        "ひ",
        "ふ",
        "へ",
        "ほ",
        "ま",
        "み",
        "む",
        "め",
        "も",
        "ゃ",
        "や",
        "ゅ",
        "ゆ",
        "ょ",
        "よ",
        "ら",
        "り",
        "る",
        "れ",
        "ろ",
        "わ",
        "を",
        "ん",
        "ァ",
        "ア",
        "ィ",
        "イ",
        "ゥ",
        "ウ",
        "ェ",
        "エ",
        "ォ",
        "オ",
        "カ",
        "キ",
        "ク",
        "ケ",
        "コ",
        "サ",
        "シ",
        "ス",
        "セ",
        "ソ",
        "タ",
        "チ",
        "ッ",
        "ツ",
        "テ",
        "ト",
        "ナ",
        "ニ",
        "ヌ",
        "ネ",
        "ノ",
        "ハ",
        "ヒ",
        "フ",
        "ヘ",
        "ホ",
        "マ",
        "ミ",
        "ム",
        "メ",
        "モ",
        "ャ",
        "ヤ",
        "ュ",
        "ユ",
        "ョ",
        "ヨ",
        "ラ",
        "リ",
        "ル",
        "レ",
        "ロ",
        "ワ",
        "ヲ",
        "ン",
        "・",
        "ー",
    ];

    let result = "";
    let i = 0;
    while (i < codes.length) {
        const c = codes[i];
        if (c === 254) {
            result += "\u3099";
            i++;
            continue;
        } // 濁点（結合文字）
        if (c === 255) {
            result += "\u309A";
            i++;
            continue;
        } // 半濁点（結合文字）
        if (c === 0 || c === 214) {
            result += " ";
        } else if (c === 211) {
            result += ")";
        } else if (c === 215) {
            result += "+";
        } else if (c === 216) {
            result += "*";
        } else if (c === 212) {
            result += "…";
        } else if (c === 213) {
            result += "■";
        } else if (c <= 94) {
            result += String.fromCharCode(c + 0x20);
        } else if (c >= 95 && c <= 210) {
            result += JP_MAP[c - 95] ?? "";
        }
        i++;
    }
    return result.normalize("NFC");
}

/**
 * window.esp8266OnCommand を登録する。
 * Rustの wasm_bindgen extern "C" が this JS function を呼び出す。
 */
export function initEsp8266CommandHandler(
    pushFn: PushResponseFn,
    discardPendingCommand: () => void = () => undefined,
): void {
    setPushResponseFn(pushFn);
    discardPendingCommandFn = discardPendingCommand;
    const bridge = window as unknown as Record<string, unknown>;
    bridge.canPollEsp8266Command = canPollEsp8266Command;
    bridge.advanceEsp8266Time = advanceEsp8266Time;

    (window as unknown as Record<string, unknown>).esp8266OnCommand = (
        bytes: Uint8Array,
    ) => {
        handleCommand(bytes);
    };
}
